import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  JSX,
  useEffect,
} from "react";
import { Toast } from "./Toast";
import { ToastItem, ToastVariant } from "./toastItem";
import { toastService } from "./toast.service";

interface ToastContextType {
  toast: {
    success: (message: string, duration?: number) => void;
    error: (message: string, duration?: number) => void;
    warning: (
      message: string,
      duration?: number,
      isAnnouncement?: boolean,
    ) => void;
    info: (message: string, duration?: number) => void;
  };
}

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context.toast;
}

interface ToastProviderProps {
  children: ReactNode;
  defaultDuration?: number;
  maxToasts?: number;
  maxAnnouncement?: number;
}

export function ToastProvider({
  children,
  defaultDuration = 5000,
  maxToasts = 3,
  maxAnnouncement = 1,
}: ToastProviderProps): JSX.Element {
  const [visible, setVisible] = useState<ToastItem[]>([]);
  const [toastQueue, setToastQueue] = useState<ToastItem[]>([]);
  const [announcementQueue, setAnnouncementQueue] = useState<ToastItem[]>([]);

  function addToast(
    message: string,
    variant: ToastVariant,
    duration?: number,
    isAnnouncement?: boolean,
  ) {

    const toast: ToastItem = {
      id:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : Date.now().toString() + Math.random().toString(36).substring(2, 9),
      message,
      variant,
      duration: duration ?? defaultDuration,
      isAnnouncement: isAnnouncement ?? false,
    };

    if (toast.isAnnouncement) {
      setAnnouncementQueue((prev) => [...prev, toast]);
      return;
    } else {
      setToastQueue((prev) => [...prev, toast]);
    }
  }

  function removeToast(id: string) {
    setVisible((prev) => prev.filter((t) => t.id !== id));
  }

  // Handle normal toast queue -> visible
  useEffect(() => {
    const visibleToastCount = visible.filter(
      (item) => !item.isAnnouncement,
    ).length;
    if (visibleToastCount >= maxToasts) return;
    if (toastQueue.length === 0) return;

    const nextToast = toastQueue[0];

    setVisible((prev) => [...prev, nextToast]);
    setToastQueue((prev) => prev.slice(1));
  }, [visible, toastQueue, maxToasts]);

  // Handle announcement queue -> visible
  useEffect(() => {
    const visibleAnnouncementCount = visible.filter(
      (item) => item.isAnnouncement,
    ).length;
    if (visibleAnnouncementCount >= maxAnnouncement) return;
    if (announcementQueue.length === 0) return;

    const nextAnnouncement = announcementQueue[0];

    setVisible((prev) => [...prev, nextAnnouncement]);
    setAnnouncementQueue((prev) => prev.slice(1));
  }, [visible, announcementQueue, maxAnnouncement]);

  useEffect(() => {
    if (toastService.isRegistered()) {
      return;
    }
    toastService.register(
      (
        message: string,
        variant: ToastVariant,
        duration?: number,
        isAnnouncement?: boolean,
      ) => {
        addToast(message, variant, duration, isAnnouncement);
      },
    );
  }, []);

  const toast = useMemo(
    () => ({
      success: (message: string, duration?: number) =>
        addToast(message, "success", duration),
      error: (message: string, duration?: number) =>
        addToast(message, "error", duration),
      warning: (message: string, duration?: number, isAnnouncement?: boolean) =>
        addToast(message, "warning", duration, isAnnouncement),
      info: (message: string, duration?: number) =>
        addToast(message, "info", duration),
    }),
    [defaultDuration],
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}

      <div className="fixed top-5 right-5 flex flex-col gap-4 z-50">
        {visible.map((toastItem) => (
          <Toast key={toastItem.id} toast={toastItem} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
